/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  computed,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  startWith,
  switchMap,
  takeUntil,
} from "rxjs";

// PrimeNG imports
import { FormsModule } from "@angular/forms";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Checkbox } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { InputTextModule } from "primeng/inputtext";
import { MenuModule } from "primeng/menu";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Services
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
  ProductSummaryUpdateResponse,
  ProductGroup,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { ProductsService } from "../../services/products.service";

// Models
import {
  BulkProductAction,
  DEFAULT_PRODUCT_COUNTS,
  IBulkProductActionRequest,
  IProductCounts,
  IProductFilterControls,
  IProductFilterValues,
  IProductListResponse,
  IProductSummary,
  ProductStatusFilter,
} from "../../models";

// Components
import {
  ProductsTableComponent,
  ProductRowAction,
  ProductAvailabilityChange,
} from "../../components/products-table/products-table.component";
import { ProductsFiltersComponent } from "../../components/products-filters/products-filters.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";

// Site interface - using the full site type with UtcOffset
import { SiteFull } from "../../../../core/models/site.interface";

/**
 * Products List Page Component
 * Main orchestrating component that manages filters, table, and bulk operations
 * Following scheduled-orders-list pattern for consistency
 */
@Component({
  selector: "app-products-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
    Checkbox,
    InputTextModule,
    ProductsTableComponent,
    ProductsFiltersComponent,
    ListPageSkeletonComponent,
  ],
  templateUrl: "./products-list.component.html",
  styleUrl: "./products-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class ProductsListComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;
  @Input() hideCreateButton: boolean = false;
  @Input() inputSearch?: string;

  // Reactive state using signals
  readonly currentSiteId = signal<number | null>(null);
  private readonly currentSite = signal<SiteFull | null>(null);
  readonly isShopifySite = signal<boolean>(false);

  // Data signals
  private readonly products = signal<IProductListResponse | null>(null);
  private readonly productCounts = signal<IProductCounts>(
    DEFAULT_PRODUCT_COUNTS,
  );
  private readonly selectedProducts = signal<IProductSummary[]>([]);
  private readonly productGroupsList = signal<ProductGroup[]>([]);

  // UI state
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false;
  readonly skipLocalStorageLoad = signal(false);

  // LocalStorage keys
  private readonly STORAGE_KEY_FILTERS = "products-list-filters";
  private readonly STORAGE_KEY_SIDEBAR = "products-list-sidebar-collapsed";

  // Loading states
  private readonly isLoading = signal(false);
  private readonly isLoadingCounts = signal(false);
  private readonly isBulkOperationLoading = signal(false);
  private readonly isInitialLoading = signal(true);

  // Track which products are currently updating availability
  private readonly updatingAvailabilityProductIds = signal<Set<number>>(
    new Set(),
  );

  // Pagination state
  private readonly currentPage = signal(1);
  private readonly currentPageSize = signal(20);

  // Sort state (matching old QMC defaults)
  private readonly currentSortField = signal("ProductName");
  private readonly currentSortOrder = signal<"asc" | "desc">("asc");

  // Form management
  filterForm!: FormGroup<IProductFilterControls>;

  // Form values signal for reactive hasActiveFilters (matching customers-list pattern)
  private readonly formValues = signal<{
    search: string | null;
    status: ProductStatusFilter;
    productGroupId: number | null;
  }>({
    search: null,
    status: "all",
    productGroupId: null,
  });

  // Computed values
  readonly data = computed(() => {
    const products = this.products();
    if (!products) return null;

    // TotalRecords now comes directly from the products service
    // which fetches both products and count in parallel with the same filters
    return products;
  });
  readonly counts = computed(() => this.productCounts());
  readonly selectedItems = computed(() => this.selectedProducts());
  readonly productGroups = computed(() => this.productGroupsList());
  readonly loading = computed(() => this.isLoading());
  readonly loadingCounts = computed(() => this.isLoadingCounts());
  readonly bulkOperationLoading = computed(() => this.isBulkOperationLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());
  readonly currentSiteData = computed(() => this.currentSite());
  readonly hasBulkSelection = computed(
    () => this.selectedProducts().length > 0,
  );
  readonly updatingAvailabilityIds = computed(() =>
    this.updatingAvailabilityProductIds(),
  );

  // Actions menu - computed to avoid double-click issues
  readonly actionsButtonLabel = computed(() => {
    const count = this.selectedProducts().length;
    return count > 0 ? `Actions (${count})` : "Actions";
  });

  readonly actionsMenuItems = computed((): MenuItem[] => {
    const count = this.selectedProducts().length;

    if (count > 0) {
      // Bulk actions for selected items
      return [
        {
          label: `Sync Prices (${count})`,
          icon: "pi pi-dollar",
          command: () => this.onBulkAction("syncPrices"),
        },
        {
          label: `Sync Sale Prices (${count})`,
          icon: "pi pi-tag",
          command: () => this.onBulkAction("syncSalePrices"),
        },
        { separator: true },
        {
          label: `Delete (${count})`,
          icon: "pi pi-trash",
          command: () => this.onBulkAction("delete"),
        },
      ];
    } else {
      // Sync all actions (no selection)
      return [
        {
          label: "Sync All Prices",
          icon: "pi pi-dollar",
          command: () => this.onSyncAllPrices(),
        },
        {
          label: "Sync All Sale Prices",
          icon: "pi pi-tag",
          command: () => this.onSyncAllSalePrices(),
        },
      ];
    }
  });

  /**
   * Computed signal to check if any filters are active
   * Uses formValues signal to ensure reactivity with OnPush change detection
   */
  readonly hasActiveFilters = computed(() => {
    // Access the signal to create a reactive dependency
    const values = this.formValues();

    return !!(
      values.search?.trim() ||
      (values.status && values.status !== "all") ||
      values.productGroupId
    );
  });

  // Selection computed
  readonly isAllSelected = computed(() => {
    const data = this.data();
    const selected = this.selectedProducts();
    if (!data?.Items?.length) return false;
    return data.Items.every((item) => selected.some((s) => s.Id === item.Id));
  });

  readonly isSomeSelected = computed(() => {
    const selected = this.selectedProducts();
    const data = this.data();
    if (!data?.Items?.length || !selected.length) return false;
    return !this.isAllSelected() && selected.length > 0;
  });

  // Component lifecycle
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly qpilotService: QPilotService,
    private readonly sessionService: SessionService,
    private readonly productsService: ProductsService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly fb: FormBuilder,
    private readonly elementRef: ElementRef,
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Handle embedded mode inputs
    const element = this.elementRef.nativeElement as HTMLElement;

    // Get siteId from input/attribute
    const siteIdString =
      this.inputSiteId?.toString() ??
      element.getAttribute("input-site-id") ??
      element.getAttribute("inputSiteId");

    if (siteIdString) {
      const siteId = parseInt(siteIdString, 10);
      this.currentSiteId.set(siteId);
    }

    // Get token from attribute and convert
    const tokenString =
      element.getAttribute("input-token") ?? element.getAttribute("inputToken");

    if (
      tokenString &&
      (!this.inputToken ||
        typeof this.inputToken !== "object" ||
        !this.inputToken.TokenBearerAuth)
    ) {
      this.inputToken = {
        TokenBearerAuth: tokenString,
        TokenAuth: "",
        Id: 0,
        UserId: 0,
        CreatedUtc: new Date(),
        UpdatedUtc: new Date(),
        ExpiresUtc: new Date(),
        User: {} as QPilotUser,
        FirstLogin: 0,
      };
    }

    // Get embeddedMode from attribute
    const embeddedModeAttr = element.getAttribute("embedded-mode");
    if (embeddedModeAttr === "true") {
      this.embeddedMode = true;
    }

    // Initialize based on mode
    // Note: setupDataSubscriptions and loadInitialData are called AFTER site data is loaded
    // to ensure UtcOffset is available for API calls (matching old QMC)
    if (this.embeddedMode && this.inputToken && this.currentSiteId()) {
      this.initializeEmbeddedMode();
    } else if (!this.embeddedMode) {
      this.initializeStandardMode();
    }

    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==================== Initialization Methods ====================

  private initializeForm(): void {
    this.filterForm = this.fb.group<IProductFilterControls>({
      search: this.fb.control(null),
      status: this.fb.nonNullable.control<ProductStatusFilter>("all"),
      productGroupId: this.fb.control(null),
    });
  }

  private initializeStandardMode(): void {
    // Get site ID from parent route parameter
    const siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
    if (siteId && siteId > 0) {
      this.currentSiteId.set(siteId);

      // Fetch site data first, then load products (to get UtcOffset for API)
      const accessToken = this.getAccessToken();
      if (accessToken) {
        this.fetchSiteDataAndInitialize(siteId, accessToken);
      }
    } else {
      console.warn("No siteId found in route parameters");
      // Still setup subscriptions even without site data
      this.setupQueryParamsSubscription();
      this.setupDataSubscriptions();
      this.loadInitialData();
    }
  }

  private initializeEmbeddedMode(): void {
    const siteId = this.currentSiteId();
    if (siteId && this.inputToken) {
      this.fetchSiteDataAndInitialize(siteId, this.inputToken);
    } else {
      console.warn("Embedded mode: missing siteId or token");
      // Still setup subscriptions even without site data
      this.setupQueryParamsSubscription();
      this.setupDataSubscriptions();
      this.loadInitialData();
    }
  }

  /**
   * Fetch site data first, then initialize data subscriptions
   * This ensures UtcOffset is available before making product API calls
   */
  private fetchSiteDataAndInitialize(
    siteId: number,
    accessToken: QPilotAccessToken,
  ): void {
    this.qpilotService
      .getSiteFull(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (site: SiteFull) => {
          this.currentSite.set(site);
          // Check for Shopify integration
          if (site.Integration?.Name?.toLowerCase().includes("shopify")) {
            this.isShopifySite.set(true);
          }
          this.cdr.markForCheck();

          // Now that site data is loaded (with UtcOffset), setup data subscriptions
          this.setupQueryParamsSubscription(); // Subscribe to queryParams changes for navigation
          this.setupDataSubscriptions();
          this.loadInitialData();
        },
        error: (error) => {
          console.error("Failed to fetch site data:", error);
          // Still setup subscriptions even if site data fails
          this.setupQueryParamsSubscription();
          this.setupDataSubscriptions();
          this.loadInitialData();
        },
      });
  }

  private initializeResponsiveSidebar(): void {
    // Auto-collapse on mobile (matching scheduled-orders pattern)
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);

    // Restore filter state from localStorage
    if (!this.skipLocalStorageLoad()) {
      this.loadFilterState();
    }
  }

  /**
   * Setup window resize listener for responsive behavior
   * Uses ResizeObserver for efficient resize detection (matching scheduled-orders pattern)
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      // Auto-collapse on mobile if user hasn't manually set preference
      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      }
      // Auto-expand on desktop if user hasn't manually collapsed
      else if (!isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(false);
      }
      // If user manually toggled, respect their preference regardless of screen size

      this.cdr.markForCheck();
    });

    resizeObserver.observe(document.body);

    // Clean up observer on destroy
    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }

  // ==================== Data Loading ====================

  /**
   * Subscribe to queryParams changes to handle navigation with ?search= params
   * This allows the search filter to be updated when navigating from other pages
   */
  private setupQueryParamsSubscription(): void {
    // Skip for embedded mode - it uses inputSearch instead
    if (this.embeddedMode) return;

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const searchParam = params["search"];
        const timestampParam = params["_t"]; // Used to force refresh

        // Only react if there's a search param AND it's different from current value
        // OR if there's a timestamp param (force refresh)
        if (searchParam !== undefined || timestampParam) {
          const currentSearch = this.filterForm.get("search")?.value;

          // If search param changed or there's a timestamp (force refresh)
          if (searchParam !== currentSearch || timestampParam) {
            this.skipLocalStorageLoad.set(true);
            this.filterForm.patchValue(
              {
                search: searchParam || "",
                status: "all",
                productGroupId: null,
              },
              { emitEvent: true }, // Emit event to trigger data reload
            );
          }
        }
      });
  }

  private setupDataSubscriptions(): void {
    // Update formValues signal on every form change (for hasActiveFilters computed)
    // This subscription runs immediately without debounce for responsive UI
    this.filterForm.valueChanges
      .pipe(startWith(this.filterForm.value), takeUntil(this.destroy$))
      .subscribe((formValue) => {
        this.formValues.set({
          search: formValue.search ?? null,
          status: formValue.status ?? "all",
          productGroupId: formValue.productGroupId ?? null,
        });
      });

    // Debounced subscription for API calls
    this.filterForm.valueChanges
      .pipe(
        startWith(this.filterForm.value),
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        switchMap((filters) => {
          const siteId = this.currentSiteId();
          const accessToken = this.getAccessToken();

          if (!siteId || !accessToken) {
            return of(null);
          }

          this.isLoading.set(true);
          this.cdr.markForCheck();

          const filterValues: IProductFilterValues = {
            search: filters.search ?? undefined,
            status: filters.status ?? "all",
            productGroupId: filters.productGroupId ?? undefined,
            page: 1,
            pageSize: 20, // Match old QMC default
          };

          return this.productsService
            .getProducts(
              siteId,
              accessToken,
              filterValues,
              this.currentSite()?.UtcOffset,
            )
            .pipe(
              catchError((error) => {
                console.error("Failed to load products:", error);
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.isLoading.set(false);
        this.isInitialLoading.set(false);

        if (response) {
          this.products.set(response);
        }

        // Save filter state to localStorage when filters change
        this.saveFilterState();

        this.cdr.markForCheck();
      });
  }

  private loadInitialData(): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) {
      this.isInitialLoading.set(false);
      return;
    }

    // Build filter values matching old QMC format
    const filterValues: IProductFilterValues = {
      page: 1,
      pageSize: 20,
      sortField: "ProductName",
      sortOrder: "asc",
      status: "all",
    };

    // Load counts with full filters (matching old QMC)
    this.isLoadingCounts.set(true);
    this.productsService
      .getProductCounts(
        siteId,
        accessToken,
        filterValues,
        this.currentSite()?.UtcOffset,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (counts) => {
          this.productCounts.set(counts);
          this.isLoadingCounts.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingCounts.set(false);
          this.cdr.markForCheck();
        },
      });

    // Load product groups for filter dropdown (matching old QMC)
    this.productsService
      .getProductGroups(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (groups) => {
          this.productGroupsList.set(groups);
          this.cdr.markForCheck();
        },
        error: () => {
          // Silently fail - product groups are optional
        },
      });
  }

  // ==================== Public Methods ====================

  getAccessToken(): QPilotAccessToken | undefined {
    return this.inputToken ?? this.sessionService.accessToken ?? undefined;
  }

  toggleSidebar(): void {
    this.userManuallyCollapsed = true;
    this.sidebarCollapsed.update((v) => !v);
    this.saveSidebarState(this.sidebarCollapsed());
  }

  // ==================== LocalStorage Methods ====================

  private saveSidebarState(collapsed: boolean): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_SIDEBAR, JSON.stringify(collapsed));
    } catch (error) {
      console.warn("Failed to save sidebar state to localStorage:", error);
    }
  }

  private loadSidebarState(): boolean | null {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY_SIDEBAR);
      if (saved !== null) {
        return JSON.parse(saved) as boolean;
      }
    } catch (error) {
      console.warn("Failed to load sidebar state from localStorage:", error);
    }
    return null;
  }

  private saveFilterState(): void {
    try {
      const values = this.filterForm.value;
      // Only save if there are active filters
      if (this.hasActiveFilters()) {
        const filterState: IProductFilterValues = {
          search: values.search ?? undefined,
          status: values.status ?? "all",
          productGroupId: values.productGroupId ?? undefined,
        };
        localStorage.setItem(
          this.STORAGE_KEY_FILTERS,
          JSON.stringify(filterState),
        );
      } else {
        // Clear saved filters if no active filters
        localStorage.removeItem(this.STORAGE_KEY_FILTERS);
      }
    } catch (error) {
      console.warn("Failed to save filter state to localStorage:", error);
    }
  }

  /**
   * Load filters into the form
   * Priority: URL query params > localStorage
   *
   * If 'search' query param exists (e.g., from product groups page):
   *   - Set search field with the query param value
   *   - Clear all other filters to defaults
   *   - Don't use localStorage
   *
   * Otherwise:
   *   - Load from localStorage as usual
   */
  private loadFilterState(): void {
    // Check for search query param (used when navigating from product groups page)
    // In embedded mode, check inputSearch attribute instead
    const searchParam = this.embeddedMode
      ? this.inputSearch
      : this.route.snapshot.queryParams["search"];

    if (searchParam) {
      // Search param exists - use only the search value, reset everything else
      // Also signal to skip loading from localStorage
      this.skipLocalStorageLoad.set(true);
      this.filterForm.patchValue(
        {
          search: searchParam,
          status: "all",
          productGroupId: null,
        },
        { emitEvent: false },
      );
      return; // Don't load from localStorage
    }

    // No search param - load from localStorage as usual
    this.loadPersistedFiltersFromStorage();
  }

  /**
   * Load persisted filters from localStorage into the form
   */
  private loadPersistedFiltersFromStorage(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY_FILTERS);
      if (saved) {
        const filterState = JSON.parse(saved) as IProductFilterValues;
        this.filterForm.patchValue({
          search: filterState.search ?? null,
          status: (filterState.status as ProductStatusFilter) ?? "all",
          productGroupId: filterState.productGroupId ?? null,
        });
      }
    } catch (error) {
      console.warn("Failed to load filter state from localStorage:", error);
    }
  }

  private clearFilterState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_FILTERS);
    } catch (error) {
      console.warn("Failed to clear filter state from localStorage:", error);
    }
  }

  toggleSelectAll(): void {
    const data = this.data();
    if (!data?.Items?.length) return;

    if (this.isAllSelected()) {
      // Deselect all on current page
      const currentIds = new Set(data.Items.map((i) => i.Id));
      this.selectedProducts.update((items) =>
        items.filter((i) => !currentIds.has(i.Id)),
      );
    } else {
      // Select all on current page
      const currentSelected = new Set(this.selectedProducts().map((i) => i.Id));
      const newItems = data.Items.filter((i) => !currentSelected.has(i.Id));
      this.selectedProducts.update((items) => [...items, ...newItems]);
    }
  }

  // Helper method to check if a product is selected (avoid arrow function in template)
  isProductSelected(product: IProductSummary): boolean {
    return this.selectedProducts().some((s) => s.Id === product.Id);
  }

  // Helper method to toggle product selection
  toggleProductSelection(selected: boolean, product: IProductSummary): void {
    if (selected) {
      this.selectedProducts.update((items) => [...items, product]);
    } else {
      this.selectedProducts.update((items) =>
        items.filter((i) => i.Id !== product.Id),
      );
    }
  }

  // Helper to get product status display text
  getProductStatusText(product: IProductSummary): string {
    if (product.Valid === false) return "Invalid";
    return product.Active ? "Active" : "Inactive";
  }

  // Helper to check product status for styling
  isProductActive(product: IProductSummary): boolean {
    return product.Active && product.Valid !== false;
  }

  isProductInactive(product: IProductSummary): boolean {
    return !product.Active;
  }

  isProductInvalid(product: IProductSummary): boolean {
    return product.Valid === false;
  }

  // ==================== Event Handlers ====================

  onFiltersChanged(filters: IProductFilterValues): void {
    // Filters are already bound via reactive form
    this.cdr.markForCheck();
  }

  onClearFilters(): void {
    this.filterForm.reset({
      search: null,
      status: "all",
      productGroupId: null,
    });
    // Clear saved filter state from localStorage
    this.clearFilterState();
  }

  onSelectionChanged(selection: IProductSummary[]): void {
    this.selectedProducts.set(selection);
  }

  onSortChanged(event: { field: string; order: number }): void {
    // Update sort state
    this.currentSortField.set(event.field);
    this.currentSortOrder.set(event.order === 1 ? "asc" : "desc");

    // Reload products with new sort params
    this.loadProductsPage(this.currentPage(), this.currentPageSize());
  }

  onPageChanged(event: {
    page: number;
    pageSize: number;
    first: number;
  }): void {
    this.currentPage.set(event.page);
    this.currentPageSize.set(event.pageSize);
    this.loadProductsPage(event.page, event.pageSize);
  }

  private loadProductsPage(page: number, pageSize: number): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    this.isLoading.set(true);
    this.cdr.markForCheck();

    const filters = this.filterForm.value;
    const filterValues: IProductFilterValues = {
      search: filters.search ?? undefined,
      status: filters.status ?? "all",
      productGroupId: filters.productGroupId ?? undefined,
      page: page,
      pageSize: pageSize,
      sortField: this.currentSortField(),
      sortOrder: this.currentSortOrder(),
    };

    this.productsService
      .getProducts(
        siteId,
        accessToken,
        filterValues,
        this.currentSite()?.UtcOffset,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          if (response) {
            this.products.set(response);
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading.set(false);
          console.error("Failed to load products:", error);
          this.cdr.markForCheck();
        },
      });
  }

  onRowAction(event: { action: string; product: IProductSummary }): void {
    const { action, product } = event;
    const siteId = this.currentSiteId();

    switch (action) {
      case "view":
        this.navigateToProduct(product.ExternalId, "view");
        break;
      case "edit":
        this.navigateToProduct(product.ExternalId, "edit");
        break;
      case "sync":
        this.handleSyncProduct(product);
        break;
      case "syncPrice":
      case "syncPrices":
        this.handleSyncPrice(product);
        break;
      case "syncSalePrice":
      case "syncSalePrices":
        this.handleSyncSalePrice(product);
        break;
      case "delete":
        this.handleDeleteProduct(product);
        break;
      default:
        console.warn("Unknown row action:", action);
    }
  }

  onAvailabilityChanged(event: ProductAvailabilityChange): void {
    this.handleAvailabilityChange(event.product, event.field, event.value);
  }

  onBulkAction(action: BulkProductAction): void {
    switch (action) {
      case "syncPrices":
        this.handleBulkSyncPrices();
        break;
      case "syncSalePrices":
        this.handleBulkSyncSalePrices();
        break;
      case "delete":
        this.handleBulkDelete();
        break;
      case "clear":
        this.selectedProducts.set([]);
        break;
    }
  }

  /**
   * Sync ALL product prices from platform
   * Uses POST /api/Products/BatchUpdateAll with BatchAction: UpdatePrice
   */
  onSyncAllPrices(): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();
    if (!siteId || !accessToken) return;

    this.isBulkOperationLoading.set(true);
    this.cdr.markForCheck();

    this.productsService
      .syncAllPrices(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isBulkOperationLoading.set(false);
          this.loadProductsPage(this.currentPage(), this.currentPageSize());
        },
        error: () => {
          this.isBulkOperationLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Sync ALL product sale prices from platform
   * Uses POST /api/Products/BatchUpdateAll with BatchAction: UpdateSalePrice
   */
  onSyncAllSalePrices(): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();
    if (!siteId || !accessToken) return;

    this.isBulkOperationLoading.set(true);
    this.cdr.markForCheck();

    this.productsService
      .syncAllSalePrices(siteId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isBulkOperationLoading.set(false);
          this.loadProductsPage(this.currentPage(), this.currentPageSize());
        },
        error: () => {
          this.isBulkOperationLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  // ==================== Navigation ====================

  private navigateToProduct(externalId: string, mode: "view" | "edit"): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for embedded mode navigation
      const event = new CustomEvent("qmc-navigate-to-product", {
        detail: {
          component: "products-list",
          action: mode,
          externalId,
          siteId: this.currentSiteId(),
        },
        bubbles: true,
        composed: true, // Required for shadow DOM crossing
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Standard navigation - uses externalId matching old QMC
      void this.router.navigate(["products", externalId], {
        relativeTo: this.route.parent,
      });
    }
  }

  // ==================== Single Product Actions ====================

  private handleSyncProduct(product: IProductSummary): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    this.productsService
      .syncProduct(siteId, product.Id, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Synced",
            detail: `Product "${product.ProductName}" synced successfully`,
            life: 3000,
          });
          this.refreshProductsList();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to sync product "${product.ProductName}"`,
            life: 5000,
          });
        },
      });
  }

  private handleSyncPrice(product: IProductSummary): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    const request: IBulkProductActionRequest = {
      ProductIds: [product.ExternalId],
      Action: "syncPrices",
    };

    this.productsService
      .bulkAction(siteId, request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Price Synced",
            detail: `Price synced for "${product.ProductName}"`,
            life: 3000,
          });
          this.refreshProductsList();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to sync price for "${product.ProductName}"`,
            life: 5000,
          });
        },
      });
  }

  private handleSyncSalePrice(product: IProductSummary): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    const request: IBulkProductActionRequest = {
      ProductIds: [product.ExternalId],
      Action: "syncSalePrices",
    };

    this.productsService
      .bulkAction(siteId, request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Sale Price Synced",
            detail: `Sale price synced for "${product.ProductName}"`,
            life: 3000,
          });
          this.refreshProductsList();
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to sync sale price for "${product.ProductName}"`,
            life: 5000,
          });
        },
      });
  }

  private handleDeleteProduct(product: IProductSummary): void {
    const accessToken = this.getAccessToken();

    if (!accessToken) return;

    // Confirm deletion
    this.confirmationService.confirm({
      message: `Are you sure you want to delete <strong>"${product.ProductName}"</strong>? This action cannot be undone.`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => {
        this.productsService
          .deleteProduct(product.Id, accessToken)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.refreshProductsList();
            },
            error: () => {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: `Failed to delete "${product.ProductName}"`,
                life: 5000,
              });
            },
          });
      },
    });
  }

  private handleAvailabilityChange(
    product: IProductSummary,
    field: "AddToScheduledOrder" | "ProcessScheduledOrder",
    value: boolean,
  ): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();

    if (!siteId || !accessToken) return;

    const fieldLabel =
      field === "AddToScheduledOrder"
        ? "Add To Scheduled Order"
        : "Process Scheduled Order";

    // Add product ID to loading set
    const currentUpdating = new Set(this.updatingAvailabilityProductIds());
    currentUpdating.add(product.Id);
    this.updatingAvailabilityProductIds.set(currentUpdating);
    this.cdr.markForCheck();

    // Pass the full product to updateAvailability (required for API endpoint)
    this.productsService
      .updateAvailability(siteId, product, field, value, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Remove product ID from loading set
          const updatedSet = new Set(this.updatingAvailabilityProductIds());
          updatedSet.delete(product.Id);
          this.updatingAvailabilityProductIds.set(updatedSet);

          // Check if the response indicates an error
          if (response.Message) {
            // Show error toast from component's MessageService (not service's)
            // This ensures toast appears in web component/embedded mode
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: response.Message,
              life: 5000,
            });
            this.cdr.markForCheck();
            return;
          }

          // Update the local product data to reflect the change immediately
          const currentData = this.products();
          if (currentData?.Items) {
            const updatedItems = currentData.Items.map((p) => {
              if (p.Id === product.Id) {
                // Use the availability from the API response
                return {
                  ...p,
                  Availability: response.Availability ?? p.Availability,
                };
              }
              return p;
            });
            this.products.set({ ...currentData, Items: updatedItems });
          }

          // Show success toast from component's MessageService
          // This ensures toast appears in web component/embedded mode
          const action = value ? "enabled" : "disabled";
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `${fieldLabel} ${action}`,
            life: 3000,
          });

          this.cdr.markForCheck();
        },
        error: () => {
          // Remove product ID from loading set on error
          const updatedSet = new Set(this.updatingAvailabilityProductIds());
          updatedSet.delete(product.Id);
          this.updatingAvailabilityProductIds.set(updatedSet);

          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to update ${fieldLabel} for "${product.ProductName}"`,
            life: 5000,
          });
          this.cdr.markForCheck();
        },
      });
  }

  // ==================== Bulk Actions ====================

  private handleBulkSyncPrices(): void {
    const selected = this.selectedProducts();
    if (!selected.length) return;

    this.confirmationService.confirm({
      message: `Sync prices for ${selected.length} product(s) from your platform?`,
      header: "Confirm Sync Prices",
      icon: "pi pi-dollar",
      accept: () => this.executeBulkAction("syncPrices"),
    });
  }

  private handleBulkSyncSalePrices(): void {
    const selected = this.selectedProducts();
    if (!selected.length) return;

    this.confirmationService.confirm({
      message: `Sync sale prices for ${selected.length} product(s) from your platform?`,
      header: "Confirm Sync Sale Prices",
      icon: "pi pi-tag",
      accept: () => this.executeBulkAction("syncSalePrices"),
    });
  }

  private handleBulkDelete(): void {
    const selected = this.selectedProducts();
    if (!selected.length) return;

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${selected.length} product(s)? This action cannot be undone.`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      accept: () => this.executeBulkAction("delete"),
    });
  }

  private executeBulkAction(
    action: "syncPrices" | "syncSalePrices" | "delete",
  ): void {
    const siteId = this.currentSiteId();
    const accessToken = this.getAccessToken();
    const selected = this.selectedProducts();

    if (!siteId || !accessToken || !selected.length) return;

    this.isBulkOperationLoading.set(true);
    this.cdr.markForCheck();

    const request: IBulkProductActionRequest = {
      ProductIds: selected.map((p) => p.ExternalId),
      Action: action,
    };

    // Get action label for toast messages (matching old QMC patterns)
    const actionLabels: Record<string, string> = {
      syncPrices: "synced prices for",
      syncSalePrices: "synced sale prices for",
      delete: "deleted",
    };
    const actionLabel = actionLabels[action] || action;

    this.productsService
      .bulkAction(siteId, request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isBulkOperationLoading.set(false);
          const count = selected.length;
          this.selectedProducts.set([]);

          // Show success toast
          if (response.SuccessCount > 0) {
            this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: `Successfully ${actionLabel} ${response.SuccessCount} product(s)`,
              life: 3000,
            });
          }

          // Show warning if some failed
          if (response.FailedCount > 0) {
            this.messageService.add({
              severity: "warn",
              summary: "Partial Success",
              detail: `Failed to ${action} ${response.FailedCount} product(s)`,
              life: 5000,
            });
          }

          // Refresh data by triggering filter form change
          this.refreshProductsList();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isBulkOperationLoading.set(false);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Failed to ${action} products. Please try again.`,
            life: 5000,
          });
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Refresh products list by triggering a data reload
   */
  private refreshProductsList(): void {
    // Directly reload the current page
    this.loadProductsPage(this.currentPage(), this.currentPageSize());
  }
}
